import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router";
import { Menu } from "lucide-react";
import { NAV, SITE } from "../../lib/site";
import { Logo } from "../ui/Logo";
import { ThemeToggle } from "../ui/ThemeToggle";
import { ButtonLink } from "../ui/Button";
import { MobileNav } from "./MobileNav";
import { cn } from "../../lib/utils";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => {
    setMenuOpen(false);
    // Return focus to the trigger (APG dialog requirement).
    triggerRef.current?.focus();
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b transition-[background-color,border-color,padding]",
        scrolled
          ? "border-line bg-surface/85 py-2 backdrop-blur-md"
          : "border-transparent bg-surface py-3.5",
      )}
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 sm:px-8">
        <Link to="/" className="rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4" aria-label={`${SITE.name} — home`}>
          <Logo />
        </Link>

        <nav aria-label="Primary" className="hidden md:block">
          <ul className="flex items-center gap-1">
            {NAV.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-ink-soft transition hover:text-ink",
                      isActive && "text-brand-600 dark:text-brand-500",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <ButtonLink to="/contact" className="hidden md:inline-flex">
            {SITE.primaryCta}
          </ButtonLink>
          <button
            ref={triggerRef}
            type="button"
            className="inline-flex size-11 items-center justify-center rounded-lg text-ink hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 md:hidden"
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            aria-label="Open navigation menu"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <MobileNav open={menuOpen} onClose={closeMenu} />
    </header>
  );
}
